/**
 * AI 资源管理页面
 */

import React, { useMemo, useCallback } from 'react';
import { ContainerPanel, CompsTable, ContainerModal, CompsForm } from '@ms/shared-components';
import { useResource } from './hooks/useResource';
import {
  getFormItems,
  getColumns,
  getPanelButtons,
  tableConfig,
  modalConfig,
  getModalFooterButtons,
  getModalTitle,
  formConfig,
  QWEN_VERSION_DEFAULT_MODELS,
} from './config';
import { getFormInitialValues } from './utils';

const ResourcePage: React.FC = () => {
  const {
    resources,
    loading,
    modalVisible,
    editingResource,
    formRef,
    setFormRef,
    loadResources,
    handleOpenModal,
    handleCloseModal,
    handleSave,
    handleDelete,
    handleTest,
  } = useResource();

  // 表单配置
  const formItems = useMemo(() => getFormItems(), []);

  // 表格列配置
  const columns = useMemo(
    () =>
      getColumns({
        onEdit: handleOpenModal,
        onDelete: handleDelete,
        onTest: handleTest,
      }),
    [handleOpenModal, handleDelete, handleTest]
  );

  // 表单初始值
  const formInitialValues = useMemo(
    () => getFormInitialValues(editingResource),
    [editingResource]
  );

  // 面板按钮配置
  const panelButtons = useMemo(
    () =>
      getPanelButtons({
        onRefresh: loadResources,
        onCreate: () => handleOpenModal(),
      }),
    [loadResources, handleOpenModal]
  );

  // 模态框底部按钮配置
  const modalFooterButtons = useMemo(
    () => getModalFooterButtons(handleCloseModal, handleSave),
    [handleCloseModal, handleSave]
  );

  // 模态框标题
  const modalTitle = useMemo(
    () => getModalTitle(!!editingResource),
    [editingResource]
  );

  // 表单值变化处理：当千问版本变化时，自动填充模型名称
  const handleFormValuesChange = useCallback(
    (changedValues: any, allValues: any) => {
      if (!formRef) return;

      // 当 qwenVersion 变化时，自动填充 model 字段
      if (changedValues.qwenVersion !== undefined && formRef) {
        const version = changedValues.qwenVersion;
        const modelTier = allValues.modelTier || 'medium';
        const versionModels = QWEN_VERSION_DEFAULT_MODELS[version];
        if (versionModels) {
          // 根据当前档位填充对应的模型名称
          const modelToSet = versionModels[modelTier] || versionModels.medium;
          if (modelToSet) {
            formRef.setFieldsValue({ model: modelToSet });
          }
        }
      }

      // 当 modelTier 变化时，如果 provider 是 qwen 且有版本，也更新 model
      if (changedValues.modelTier !== undefined && formRef) {
        const provider = allValues.provider || formRef.getFieldValue('provider');
        const version = allValues.qwenVersion || formRef.getFieldValue('qwenVersion');
        if (provider === 'qwen' && version) {
          const versionModels = QWEN_VERSION_DEFAULT_MODELS[version];
          if (versionModels) {
            const tier = changedValues.modelTier;
            const modelToSet = versionModels[tier] || versionModels.medium;
            if (modelToSet) {
              formRef.setFieldsValue({ model: modelToSet });
            }
          }
        }
      }
    },
    [formRef]
  );

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <ContainerPanel title="AI 资源管理" buttons={panelButtons} height="100%">
        <CompsTable
          resizableColumns
          storageKey="msai-resource"
          tableProps={{
            columns: columns as any,
            dataSource: resources,
            rowKey: tableConfig.rowKey,
            loading,
            pagination: tableConfig.pagination,
          } as any}
        />
      </ContainerPanel>

      {/* 创建/编辑模态框 */}
      <ContainerModal
        title={modalTitle}
        visible={modalVisible}
        onCancel={handleCloseModal}
        width={modalConfig.width}
        height={modalConfig.height}
        contentPadding={modalConfig.contentPadding}
        footerRightButtons={modalFooterButtons}
        modalProps={modalConfig.modalProps}
      >
        <CompsForm
          formItems={formItems}
          formRef={setFormRef}
          initialValues={formInitialValues}
          layout={formConfig.layout}
          contentPadding={formConfig.contentPadding}
          onValuesChange={handleFormValuesChange}
        />
      </ContainerModal>
    </div>
  );
};

export default ResourcePage;
